import InternalService from '../../core/services/InternalService';

/**
 * Type definition for service method functions
 * These methods are used for initialization and refresh operations
 */
type ServiceMethodType = (...args: unknown[]) => Promise<unknown> | unknown;

/**
 * Interface representing initialization and refresh methods for a refreshable service
 */
export type ReloadedMethodsType = {
  /** Method to initialize the service */
  init: ServiceMethodType;
  /** Method to refresh the service when context changes */
  refresh: ServiceMethodType;
};

/**
 * Base class for services that need to be initialized and refreshed
 * This class is used with the Container system to manage service lifecycle
 */
export class Refreshable extends InternalService {
  /** The initialization method for this service */
  private _refInit: ServiceMethodType;

  /** The refresh method for this service */
  private _refRefresh: ServiceMethodType;

  constructor() {
    super();
    this._refInit = undefined;
    this._refRefresh = undefined;
  }

  /**
   * Sets the initialization and refresh methods for this service
   * @param init - The method to initialize the service
   * @param refresh - The method to refresh the service when context changes
   */
  public setRefreshMethods({ init, refresh }: ReloadedMethodsType): void {
    this._refInit = init;
    this._refRefresh = refresh;
  }

  /**
   * Gets the initialization and refresh methods for this service
   * @returns An object containing the init and refresh methods
   */
  public getRefreshMethods(): ReloadedMethodsType {
    return {
      init: this._refInit,
      refresh: this._refRefresh
    };
  }
}
